import { useTranslation } from 'react-i18next';
import { getFundamentalName, getHabitName } from '@/hooks/useFundamentals';
import type { FundamentalCatalogItem } from '@/api/types';
import { TIP_TYPES } from '@/data/tipTypes';
import { fmtCond } from '@/utils/formatters';

// ── Shared types ─────────────────────────────────────────────────────────────
export interface LangText { name: string; tipText: string; crecerPaired: string; }

export interface TipFormState {
  open: boolean;
  editId: string | null;
  fund: string;
  behavior: string;
  thrMin: string;
  thrMax: string;
  tipType: 'reconocer' | 'crecer';
  name: string;
  tipText: string;
  crecerPaired: string;
  active: boolean;
  addedLangs: string[];
  previewLang: string;
  langTexts: Record<string, LangText>;
}

export interface DeleteTipTarget { id: string; name: string; }

// ── Shared constants ──────────────────────────────────────────────────────────
export const TRANS_LANGS = [
  { code: 'en', flag: '🇺🇸', label: 'EN' },
  { code: 'fr', flag: '🇫🇷', label: 'FR' },
  { code: 'pt', flag: '🇧🇷', label: 'PT' },
  { code: 'de', flag: '🇩🇪', label: 'DE' },
  { code: 'nl', flag: '🇳🇱', label: 'NL' },
  { code: 'ro', flag: '🇷🇴', label: 'RO' },
];

export function defaultLangTexts(): Record<string, LangText> {
  return Object.fromEntries(TRANS_LANGS.map(l => [l.code, { name: '', tipText: '', crecerPaired: '' }]));
}

export const ALL_LANG_CODES = TRANS_LANGS.map(l => l.code);

// ── Props ─────────────────────────────────────────────────────────────────────
interface TipFormModalProps {
  form: TipFormState;
  setForm: React.Dispatch<React.SetStateAction<TipFormState>>;
  saveErrors: string[];
  onClose: () => void;
  onSave: () => void;
  fundamentals: FundamentalCatalogItem[];
}

export function TipFormModal({ form, setForm, saveErrors, onClose, onSave, fundamentals }: TipFormModalProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split('-')[0];

  // ── Language handlers ────────────────────────────────────────────────────────
  function addLang(code: string) {
    if (form.addedLangs.includes(code)) return;
    if (form.addedLangs.length === 0 && import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[Tips] request translations via Azure Translator API', { tipText: form.tipText, crecerPaired: form.crecerPaired, name: form.name });
    }
    setForm(f => ({
      ...f,
      addedLangs: [...f.addedLangs, code],
      langTexts: { ...f.langTexts, [code]: f.langTexts[code] ?? { name: '', tipText: '', crecerPaired: '' } },
    }));
  }

  function removeLang(code: string) {
    setForm(f => ({
      ...f,
      addedLangs: f.addedLangs.filter(l => l !== code),
      previewLang: f.previewLang === code ? 'es' : f.previewLang,
    }));
  }

  function updateLangText(code: string, field: keyof LangText, value: string) {
    setForm(f => ({
      ...f,
      langTexts: {
        ...f.langTexts,
        [code]: { ...(f.langTexts[code] ?? { name: '', tipText: '', crecerPaired: '' }), [field]: value },
      },
    }));
  }

  // ── Derived preview values ────────────────────────────────────────────────────
  const prevFund = fundamentals.find(f => f.id === form.fund);
  const prevCond = form.thrMin !== '' && form.thrMax !== ''
    ? fmtCond('between', Number(form.thrMin), Number(form.thrMax))
    : null;
  const prevType = TIP_TYPES.find(tt => tt.id === form.tipType);

  const isEsPreview = form.previewLang === 'es';
  const prevLangTexts = isEsPreview ? null : (form.langTexts[form.previewLang] ?? null);
  const prevName = isEsPreview ? form.name : (prevLangTexts?.name || form.name);
  const prevTipText = isEsPreview ? form.tipText : (prevLangTexts?.tipText || form.tipText);
  const prevCrecerPaired = isEsPreview ? form.crecerPaired : (prevLangTexts?.crecerPaired || form.crecerPaired);

  const reconocerEntry = TIP_TYPES.find(tt => tt.id === 'reconocer');
  const crecerEntry = TIP_TYPES.find(tt => tt.id === 'crecer');
  const prevReconocerLabel = reconocerEntry?.labels[form.previewLang] ?? reconocerEntry?.labels['es'] ?? 'Para Reconocer';
  const prevCrecerLabel = crecerEntry?.labels[form.previewLang] ?? crecerEntry?.labels['es'] ?? 'Para Crecer';

  const behaviorsFor = (fundId: string) => fundamentals.find(f => f.id === fundId)?.dailyHabits.filter(h => h.isActive) ?? [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="tif-box" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="tif-header">
          <h3>
            <i className={`fas ${prevType?.icon ?? 'fa-lightbulb'}`}
               style={{ color: form.tipType === 'reconocer' ? 'var(--cp-red)' : '#1e40af' }} />
            {form.editId ? t('admin.tipEditTitle') : t('admin.tipAddTitle')}
          </h3>
          <button className="tif-close" onClick={onClose} aria-label={t('common.close')}>
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Split body */}
        <div className="tif-split">

          {/* ── Form column ── */}
          <div className="tif-form-col">

            {/* Fundamental */}
            <div className="tif-field">
              <label className="tif-label">{t('admin.tipFormFundamental')}</label>
              <select className="form-input tif-select" value={form.fund}
                onChange={e => setForm(f => ({ ...f, fund: e.target.value, behavior: '' }))}>
                <option value="">{t('admin.tipFormSelectFundamental')}</option>
                {fundamentals.map(f => (
                  <option key={f.id} value={f.id}>{getFundamentalName(f, lang)}</option>
                ))}
              </select>
            </div>

            {/* Core Behavior */}
            <div className="tif-field">
              <label className="tif-label">{t('admin.tipFormDailyHabit')}</label>
              <select className="form-input tif-select" value={form.behavior}
                onChange={e => setForm(f => ({ ...f, behavior: e.target.value }))}>
                <option value="">{t('admin.tipFormSelectDailyHabit')}</option>
                {behaviorsFor(form.fund).map(b => (
                  <option key={b.id} value={b.id}>{getHabitName(b, lang)}</option>
                ))}
              </select>
              <span className="tif-hint">{t('admin.tipHintBehavior')}</span>
            </div>

            {/* Condition range */}
            <div className="tif-field">
              <label className="tif-label">{t('admin.tipFormActivationRule')}</label>
              <div className="tif-range-row">
                <div className="tif-range-field">
                  <span className="tif-range-label">{t('admin.tipRangeMin')}</span>
                  <input type="number" className="form-input tif-num" min={0} max={100} step={0.1}
                    placeholder="0" value={form.thrMin}
                    onChange={e => setForm(f => ({ ...f, thrMin: e.target.value }))} />
                </div>
                <span className="tif-range-dash">—</span>
                <div className="tif-range-field">
                  <span className="tif-range-label">{t('admin.tipRangeMax')}</span>
                  <input type="number" className="form-input tif-num" min={0} max={100} step={0.1}
                    placeholder="100" value={form.thrMax}
                    onChange={e => setForm(f => ({ ...f, thrMax: e.target.value }))} />
                </div>
              </div>
              <span className="tif-hint">{t('admin.tipHintCondition')}</span>
            </div>

            {/* Type cards */}
            <div className="tif-field">
              <label className="tif-label">{t('admin.tipFormType')}</label>
              <div className="tif-type-cards">
                {TIP_TYPES.map(tt => (
                  <label key={tt.id} className="tif-type-card">
                    <input type="radio" name={`tip-form-type-${form.editId ?? 'new'}`}
                      value={tt.id} checked={form.tipType === tt.id}
                      onChange={() => setForm(f => ({ ...f, tipType: tt.id }))} />
                    <div className="tif-type-card-body">
                      <div className={`tif-type-card-icon ${tt.iconClass}`}>
                        <i className={`fas ${tt.icon}`} />
                      </div>
                      <div className="tif-type-card-info">
                        <div className="tif-type-card-name">
                          {tt.labels[lang] ?? tt.labels['es']}
                        </div>
                        <div className="tif-type-card-desc">
                          {tt.descriptions[lang] ?? tt.descriptions['es']}
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Conditional content fields */}
            {form.tipType === 'reconocer' && (
              <>
                <div className="tif-field">
                  <label className="tif-label">{t('admin.tipFormName')}</label>
                  <input type="text" className="form-input" value={form.name}
                    placeholder={t('admin.tipFormNamePlaceholder')}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div className="tif-field">
                  <label className="tif-label">
                    {t('admin.tipFieldObserved')}
                    <span className="tif-field-tag ttf-tag-green">{t('admin.tipTagGreen')}</span>
                  </label>
                  <textarea className="form-input tif-textarea" rows={3} value={form.tipText}
                    placeholder={t('admin.tipFormTextPlaceholder')}
                    onChange={e => setForm(f => ({ ...f, tipText: e.target.value }))} />
                  <span className="tif-hint">{t('admin.tipHintObserved')}</span>
                </div>
                <div className="tif-field">
                  <label className="tif-label">
                    {t('admin.tipFieldGrowthSuggestion')}
                    <span className="tif-field-tag ttf-tag-dark">{t('admin.tipTagDark')}</span>
                  </label>
                  <textarea className="form-input tif-textarea" rows={3} value={form.crecerPaired}
                    placeholder={t('admin.tipCrecerPairedPlaceholder')}
                    onChange={e => setForm(f => ({ ...f, crecerPaired: e.target.value }))} />
                  <span className="tif-hint">{t('admin.tipHintGrowthSuggestion')}</span>
                </div>
              </>
            )}

            {form.tipType === 'crecer' && (
              <>
                <div className="tif-field">
                  <label className="tif-label">{t('admin.tipFieldLevelName')}</label>
                  <input type="text" className="form-input" value={form.name}
                    placeholder={t('admin.tipLevelNamePlaceholder')}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div className="tif-field">
                  <label className="tif-label">
                    {t('admin.tipFormText')}
                    <span className="tif-field-tag ttf-tag-blue">{t('admin.tipTagBlue')}</span>
                  </label>
                  <textarea className="form-input tif-textarea" rows={3} value={form.tipText}
                    placeholder={t('admin.tipFormTextPlaceholder')}
                    onChange={e => setForm(f => ({ ...f, tipText: e.target.value }))} />
                </div>
              </>
            )}

            {/* Translations */}
            <div className="tif-field tif-lang-section">
              <div className="tif-lang-header">
                <label className="tif-label">
                  <i className="fas fa-language" /> {t('admin.tipTranslations')}
                </label>
              </div>
              <div className="tif-lang-add-row">
                <span className="tif-lang-add-label">{t('admin.tipAddLang')}</span>
                {TRANS_LANGS.map(lc => (
                  <button
                    key={lc.code} type="button"
                    className={`tif-lang-quick-btn${form.addedLangs.includes(lc.code) ? ' tif-lang-used' : ''}`}
                    onClick={() => addLang(lc.code)}
                  >
                    {lc.flag} {lc.label}
                  </button>
                ))}
              </div>
              {form.addedLangs.length > 0 && (
                <div className="tif-lang-list">
                  {form.addedLangs.map(code => {
                    const lc = TRANS_LANGS.find(l => l.code === code);
                    const lt = form.langTexts[code] ?? { name: '', tipText: '', crecerPaired: '' };
                    return (
                      <div key={code} className="tif-lang-block">
                        <div className="tif-lang-block-head">
                          <span className="tif-lang-block-label">{lc?.flag} {lc?.label ?? code.toUpperCase()}</span>
                          <button type="button" className="tif-lang-block-del"
                            onClick={() => removeLang(code)} title={t('common.delete')}>
                            <i className="fas fa-times" />
                          </button>
                        </div>
                        <div className="tif-lang-block-body">
                          {form.tipType === 'reconocer' ? (
                            <>
                              <div className="tif-field" style={{ gap: 4 }}>
                                <label className="tif-label" style={{ fontSize: '0.75rem' }}>
                                  {t('admin.tipFieldObserved')}
                                  <span className="tif-field-tag ttf-tag-green">{t('admin.tipTagGreen')}</span>
                                </label>
                                <textarea className="form-input tif-textarea" rows={2} value={lt.tipText}
                                  placeholder={t('admin.tipFormTextPlaceholder')}
                                  onChange={e => updateLangText(code, 'tipText', e.target.value)} />
                              </div>
                              <div className="tif-field" style={{ gap: 4 }}>
                                <label className="tif-label" style={{ fontSize: '0.75rem' }}>
                                  {t('admin.tipFieldGrowthSuggestion')}
                                  <span className="tif-field-tag ttf-tag-dark">{t('admin.tipTagDark')}</span>
                                </label>
                                <textarea className="form-input tif-textarea" rows={2} value={lt.crecerPaired}
                                  placeholder={t('admin.tipCrecerPairedPlaceholder')}
                                  onChange={e => updateLangText(code, 'crecerPaired', e.target.value)} />
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="tif-field" style={{ gap: 4 }}>
                                <label className="tif-label" style={{ fontSize: '0.75rem' }}>{t('admin.tipFieldLevelName')}</label>
                                <input type="text" className="form-input" value={lt.name}
                                  placeholder={t('admin.tipLevelNamePlaceholder')}
                                  onChange={e => updateLangText(code, 'name', e.target.value)} />
                              </div>
                              <div className="tif-field" style={{ gap: 4 }}>
                                <label className="tif-label" style={{ fontSize: '0.75rem' }}>
                                  {t('admin.tipFormText')}
                                  <span className="tif-field-tag ttf-tag-blue">{t('admin.tipTagBlue')}</span>
                                </label>
                                <textarea className="form-input tif-textarea" rows={2} value={lt.tipText}
                                  placeholder={t('admin.tipFormTextPlaceholder')}
                                  onChange={e => updateLangText(code, 'tipText', e.target.value)} />
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Active */}
            <div className="tif-field tif-active-row">
              <label className="switch">
                <input type="checkbox" checked={form.active}
                  onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} />
                <span className="slider" />
              </label>
              <span className="tif-active-label">{t('admin.tipFormActive')}</span>
            </div>
          </div>

          {/* ── Preview column ── */}
          <div className="tif-preview-col">
            <div className="tif-preview-label">
              <span><i className="fas fa-eye" /> {t('admin.tipFormPreview')}</span>
            </div>
            <div className="tif-preview-context">{t('admin.tipPreviewContext')}</div>

            {/* Language switcher */}
            <div className="acf-lang-row" style={{ marginBottom: 8 }}>
              <button type="button"
                className={`acf-lang-btn${form.previewLang === 'es' ? ' active' : ''}`}
                onClick={() => setForm(f => ({ ...f, previewLang: 'es' }))}>
                🇪🇸 ES
              </button>
              {form.addedLangs.map(code => {
                const lc = TRANS_LANGS.find(l => l.code === code);
                return (
                  <button key={code} type="button"
                    className={`acf-lang-btn${form.previewLang === code ? ' active' : ''}`}
                    onClick={() => setForm(f => ({ ...f, previewLang: code }))}>
                    {lc?.flag} {lc?.label ?? code.toUpperCase()}
                  </button>
                );
              })}
            </div>

            {form.tipType === 'reconocer' ? (
              <div className="tif-preview-card">
                <div className="acf-prev-rec-header">
                  {prevFund && (
                    <span className="acf-prev-fund-chip" style={{ background: `${prevFund.color}22`, color: prevFund.color }}>
                      <i className="fas fa-circle" style={{ fontSize: '0.5rem' }} /> {getFundamentalName(prevFund, lang)}
                    </span>
                  )}
                  <span className="acf-prev-consist-chip">CONSISTENTE</span>
                </div>
                {prevName && <div className="tif-preview-name">{prevName}</div>}
                <div className="acf-prev-rec-pills">
                  <div className="acf-prev-rec-pill r">
                    <div className="acf-prev-rec-label">
                      <i className="fas fa-compass" /> {prevReconocerLabel.toUpperCase()}
                    </div>
                    <div className="acf-prev-rec-text">
                      {prevTipText || <span style={{ opacity: 0.4 }}>{t('admin.tipFormTextPlaceholder')}</span>}
                    </div>
                  </div>
                  <div className="acf-prev-rec-pill c">
                    <div className="acf-prev-rec-label">
                      <i className="fas fa-seedling" /> {prevCrecerLabel.toUpperCase()}
                    </div>
                    <div className="acf-prev-rec-text">
                      {prevCrecerPaired || <span style={{ opacity: 0.3 }}>{t('admin.tipCrecerPairedPlaceholder')}</span>}
                    </div>
                  </div>
                </div>
                {prevCond && <div className="tif-preview-cond">{prevCond}</div>}
              </div>
            ) : (
              <div className="tif-preview-card">
                <div className="acf-prev-toprow">
                  <div className="acf-prev-icon"
                    style={{ background: `${prevFund?.color ?? '#9ca3af'}22`, border: `2px solid ${prevFund?.color ?? '#9ca3af'}44` }}>
                    <i className="fas fa-circle-half-stroke" style={{ color: prevFund?.color ?? 'var(--text-muted)', fontSize: '1rem' }} />
                  </div>
                  <div className="acf-prev-fundinfo">
                    <div className="acf-prev-fundname">{prevFund ? getFundamentalName(prevFund, lang) : '—'}</div>
                    <div className="acf-prev-status" style={{ color: prevFund?.color ?? 'var(--text-muted)' }}>
                      En Desarrollo
                    </div>
                  </div>
                  <div className="acf-prev-score" style={{ color: prevFund?.color ?? 'var(--text-muted)' }}>
                    1.8<span className="acf-prev-denom">/4</span>
                  </div>
                </div>

                <div className="acf-prev-bar">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className="acf-prev-seg"
                      style={{ background: i <= 2 && prevFund ? prevFund.color : '#e2e4e9' }} />
                  ))}
                </div>

                {prevTipText
                  ? <div className="acf-prev-next-box">
                      <div className="acf-prev-next-label">SIGUIENTE NIVEL</div>
                      <div className="acf-prev-next-text">{prevTipText}</div>
                    </div>
                  : <div className="acf-prev-placeholder">← {t('admin.tipFormTextPlaceholder')}</div>}

                {prevName
                  ? <div className="acf-prev-action-row">
                      <i className="fas fa-bolt" style={{ color: prevFund?.color ?? 'var(--text-muted)' }} />
                      <span className="acf-prev-action-title" style={{ color: prevFund?.color ?? 'var(--text-muted)' }}>{prevName}</span>
                    </div>
                  : <div className="acf-prev-placeholder">← {t('admin.tipLevelNamePlaceholder')}</div>}

                {prevCond && <div className="tif-preview-cond">{prevCond}</div>}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="tif-footer">
          {saveErrors.length > 0 && (
            <div className="tif-save-errors">
              <strong><i className="fas fa-exclamation-circle" /> {t('admin.tipSaveError')}</strong>
              <ul>
                {saveErrors.map((err, i) => <li key={i}>{err}</li>)}
              </ul>
            </div>
          )}
          <div className="tif-footer-actions">
            <button className="btn btn--ghost" onClick={onClose}>{t('common.cancel')}</button>
            <button className="btn btn--primary" onClick={onSave}>
              <i className="fas fa-check" /> {t('admin.tipSave')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
